import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/Spinner';
import { revokeSupportAccessRequest } from '../../lib/supportAccessClient';
import { supabase } from '../../lib/supabase';
import type { SupportAccessRequestRow } from '../../types/supportAccess';
import { cardClass } from './styles';

type Props = {
  userId: string;
  onToast: (message: string, variant: 'success' | 'error') => void;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; rows: SupportAccessRequestRow[] };

export function SupportAccessSettingsSection({ userId, onToast }: Props) {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { data, error } = await supabase
      .from('support_access_requests')
      .select(
        'id, user_id, session_token, reason, status, requested_at, expires_at, approved_at, revoked_at, revoked_by'
      )
      .eq('user_id', userId)
      .order('requested_at', { ascending: false });

    if (error) {
      setState({ kind: 'error', message: error.message });
      return;
    }
    setState({ kind: 'ready', rows: (data ?? []) as SupportAccessRequestRow[] });
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = state.kind === 'ready' ? state.rows.find((r) => r.status === 'active') ?? null : null;
  const count = state.kind === 'ready' ? state.rows.length : 0;

  const handleConfirmRevoke = async () => {
    if (!active) return;
    setRevokeBusy(true);
    const { errorMessage } = await revokeSupportAccessRequest({ requestId: active.id, userId });
    setRevokeBusy(false);
    setRevokeOpen(false);
    if (errorMessage) {
      onToast(errorMessage, 'error');
      return;
    }
    onToast('Support access revoked.', 'success');
    void load();
  };

  return (
    <>
      <section className={cardClass}>
        <h3 className="text-lg font-semibold text-foreground">Support Access</h3>
        <p className="mt-2 text-sm text-muted">
          Approve or revoke temporary logNsec administrator access to your account (Level 3 customer consent).
        </p>

        {state.kind === 'loading' ? (
          <div className="mt-6 flex items-center gap-3">
            <Spinner className="h-5 w-5 text-accent" />
            <span className="text-sm text-muted">Loading…</span>
          </div>
        ) : null}

        {state.kind === 'error' ? (
          <p className="mt-4 text-sm text-danger" role="alert">
            {state.message}
          </p>
        ) : null}

        {state.kind === 'ready' ? (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-foreground">
              <span className="font-semibold">{count}</span> support access request{count === 1 ? '' : 's'} on file.
            </p>

            {active ? (
              <div className="rounded-xl border border-danger/45 bg-danger/10 px-4 py-3">
                <p className="text-sm font-semibold text-danger">Active support session</p>
                <p className="mt-1 text-sm text-muted">
                  A logNsec agent may have read access to your dashboard until this session expires or you revoke it.
                </p>
              </div>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="button"
                onClick={() => navigate('/settings/support-access')}
                className="rounded-lg border border-accent/50 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20"
              >
                Manage Support Access
              </button>
              {active ? (
                <button
                  type="button"
                  onClick={() => setRevokeOpen(true)}
                  className="rounded-lg border-2 border-danger px-4 py-2.5 text-sm font-semibold text-danger transition hover:bg-danger/10"
                >
                  Revoke Active Session
                </button>
              ) : null}
            </div>

            <p className="text-xs text-muted">
              Prefer email? Links in logNsec notifications open{' '}
              <Link to="/settings/support-access" className="font-medium text-accent hover:underline">
                this page
              </Link>
              .
            </p>
          </div>
        ) : null}
      </section>

      {revokeOpen && active ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="settings-revoke-support-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-xl">
            <h2 id="settings-revoke-support-title" className="text-lg font-semibold text-foreground">
              Revoke access?
            </h2>
            <p className="mt-3 text-sm text-muted">
              Are you sure you want to revoke admin access to your account?
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setRevokeOpen(false)}
                className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={revokeBusy}
                onClick={() => void handleConfirmRevoke()}
                className="rounded-lg bg-danger px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-danger/90 disabled:opacity-60"
              >
                {revokeBusy ? 'Revoking…' : 'Revoke access'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
